import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { InputText } from 'primereact/inputtext'
import { ProgressSpinner } from 'primereact/progressspinner'
import { classNames } from 'primereact/utils'
import useNotify from '../../hooks/useNotify'
import useResizeListener from '../../hooks/useResizeListener'
import { getFieldErrorMessage, parseApiError } from '../../services/apiError'
import { generateNursingShifts, getNursingShiftHours } from '../../services/NursingShiftController'
import { NursingShiftDTO, NursingShiftHoursDTO } from '../../dto/nursingShift/NursingShiftDTO'
import './NursingShiftHoursView.scss'

const START_HOUR_INPUT_ID = 'nursing-shift-start-hour'
const START_HOUR_ERROR_ID = 'nursing-shift-start-hour-error'
const HOUR_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/
/** Errores del backend que corresponden al campo hora de inicio: se muestran como marcador, no como card. */
const FIELD_ERROR_CODES = ['VALIDATION_ERROR', 'INVALID_NURSING_SHIFT_START_HOUR']

/**
 * Formatea lo escrito a HH:mm: "6" → "06:00", "630" → "06:30", "1430" → "14:30", "6:5" → "06:05", "14:" → "14:00".
 * Si no se puede interpretar, lo devuelve como está (y la validación lo marca).
 */
function formatStartHour(raw: string): string {
  const value = raw.trim()
  if (value === '') return ''
  let hours: string
  let minutes: string
  if (value.includes(':')) {
    [hours, minutes = ''] = value.split(':')
  } else if (value.length <= 2) {
    hours = value
    minutes = ''
  } else {
    hours = value.slice(0, -2)
    minutes = value.slice(-2)
  }
  if (!/^\d{1,2}$/.test(hours) || !/^\d{0,2}$/.test(minutes)) return value
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
}

/** Cabecera de la columna de un turno: "Turno mañana" → "Mañana" (la tabla ya es de turnos, no se repite la palabra). */
function shiftColumnLabel(description: string): string {
  const label = description.replace(/^turno\s+/i, '').trim()
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** Novarium (notificaciones.md): la leyenda dice qué hacer. */
function validateStartHour(value: string): string | null {
  if (value.trim() === '') return 'Completá la hora de inicio'
  if (!HOUR_PATTERN.test(value)) return 'Ingresá una hora entre 00:00 y 23:59'
  return null
}

/** Parámetros > Horarios de enfermería: la hora de inicio de los turnos del sitio; los 3 turnos son de 8 horas. */
export default function NursingShiftHoursView() {
  const notify = useNotify()
  // componentes.md "Tablas en mobile": hasta 768px la tabla pasa a cards
  const isMobile = useResizeListener(768)
  const inputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedStartHour, setSavedStartHour] = useState('')
  const [startHour, setStartHour] = useState('')
  const [intervalHours, setIntervalHours] = useState<number | null>(null)
  const [shifts, setShifts] = useState<NursingShiftDTO[]>([])
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  function applyHours(hours: NursingShiftHoursDTO) {
    setSavedStartHour(hours.startHour)
    setStartHour(hours.startHour)
    setIntervalHours(hours.intervalHours)
    setShifts(hours.shifts)
  }

  useEffect(() => {
    let cancelled = false
    getNursingShiftHours()
      .then((hours) => {
        if (!cancelled) applyHours(hours)
      })
      .catch((err: unknown) => {
        parseApiError(err)
        if (!cancelled) notify.error('No se pudieron cargar los horarios de enfermería. Intentá de nuevo.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [notify])

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    // solo números y ":" (HH:mm son 5 caracteres)
    const value = event.target.value.replace(/[^\d:]/g, '').slice(0, 5)
    setStartHour(value)
    // notificaciones.md: el marcador se borra apenas el valor queda bien
    if (error !== null && validateStartHour(formatStartHour(value)) === null) setError(null)
  }

  function handleBlur() {
    const formatted = formatStartHour(startHour)
    setStartHour(formatted)
    setError(validateStartHour(formatted))
  }

  function showError(message: string) {
    setError(message)
    inputRef.current?.focus()
  }

  async function handleGenerate() {
    const formatted = formatStartHour(startHour)
    setStartHour(formatted)
    const validationMessage = validateStartHour(formatted)
    if (validationMessage !== null) {
      showError(validationMessage)
      return
    }

    // Misma hora que la guardada: no se escribe nada, pero se confirma igual
    if (formatted === savedStartHour) {
      setError(null)
      setEditing(false)
      notify.success('Datos guardados correctamente')
      return
    }

    setSaving(true)
    try {
      applyHours(await generateNursingShifts(formatted))
      setError(null)
      setEditing(false)
      notify.success('Datos guardados correctamente')
    } catch (err: unknown) {
      const apiError = parseApiError(err)
      if (FIELD_ERROR_CODES.includes(apiError.errorCode)) {
        showError(getFieldErrorMessage(apiError, 'startHour') ?? apiError.message)
      } else {
        notify.error('No se pudieron guardar los horarios de enfermería. Intentá de nuevo.')
      }
    } finally {
      setSaving(false)
    }
  }

  // El input arranca deshabilitado: "Editar" lo habilita y el botón pasa a "Generar", que guarda
  function handleEdit() {
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div className='viewContainer nursing-shift-hours-view'>
      <h1 className='nv-h1'>Horarios de enfermería</h1>

      {loading ? (
        <div className='nv-loading' role='status'>
          <ProgressSpinner strokeWidth='4' />
          <span>Cargando…</span>
        </div>
      ) : (
        <>
          <div className='nv-form-row'>
            <div className='nv-field nursing-shift-hours-field'>
              <label htmlFor={START_HOUR_INPUT_ID}>
                Hora de inicio <span className='nv-req'>*</span>
              </label>
              <InputText
                id={START_HOUR_INPUT_ID}
                ref={inputRef}
                value={startHour}
                placeholder='Ej.: 06:00'
                inputMode='numeric'
                maxLength={5}
                autoComplete='off'
                disabled={!editing || saving}
                className={classNames({ 'p-filled': startHour !== '', 'p-invalid': error !== null })}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={error !== null || undefined}
                aria-describedby={error !== null ? START_HOUR_ERROR_ID : undefined}
              />
              {error !== null && (
                <small id={START_HOUR_ERROR_ID} className='p-error'>
                  <i className='pi pi-exclamation-triangle' aria-hidden='true' />
                  {error}
                </small>
              )}
            </div>

            <div className='nursing-shift-hours-action'>
              <Button
                type='button'
                label={editing ? 'Generar' : 'Editar'}
                className='buttonMain'
                disabled={saving}
                onClick={editing ? () => { void handleGenerate() } : handleEdit}
              />
            </div>
          </div>

          {isMobile ? (
            // Mobile: una card por turno con su horario desde/hasta
            <div className='nv-m-card-list'>
              {shifts.map((shift) => (
                <div key={shift.nursingShiftTypeId} className='nv-m-card'>
                  <div className='nv-m-card-header'>
                    <h2 className='nv-m-card-title'>{shiftColumnLabel(shift.description)}</h2>
                  </div>
                  <div className='nv-m-card-row'>
                    <span className='nv-m-card-key'>Desde</span>
                    <span className='nv-m-card-value'>{shift.from}</span>
                  </div>
                  <div className='nv-m-card-row'>
                    <span className='nv-m-card-key'>Hasta</span>
                    <span className='nv-m-card-value'>{shift.to}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
          // Escritorio: una columna por turno (Mañana, Tarde, Noche) con su horario, en una sola fila
          <DataTable
            value={shifts.length > 0 ? [{ id: 'hours' }] : []}
            dataKey='id'
            className='nursing-shift-hours-table'
            emptyMessage='—'
          >
            {shifts.map((shift) => (
              <Column
                key={shift.nursingShiftTypeId}
                header={shiftColumnLabel(shift.description)}
                body={() => `${shift.from} – ${shift.to}`}
                align='center'
                alignHeader='center'
              />
            ))}
          </DataTable>
          )}
          {intervalHours !== null && (
            <p className='nursing-shift-hours-note'>El intervalo es de {intervalHours} hs</p>
          )}
        </>
      )}
    </div>
  )
}
